import { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Building2,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  TrendingDown,
  TrendingUp,
  Scale,
  AlertCircle,
  Loader2,
  History,
  Layers,
  Cloud,
  Package,
} from "lucide-react";
import { cn, formatEur } from "@/lib/utils";
import TablaDrivers from "./TablaDrivers";
import {
  getProyeccionOperaciones,
  getProyeccionResultado,
  crearProyeccionOperacion,
  actualizarProyeccionOperacion,
  eliminarProyeccionOperacion,
} from "@/lib/api";

// ---------------------------------------------------------------------------
// Formatos
// ---------------------------------------------------------------------------

// Formato único de proyección: una sola fuente en lib/utils (formatEur).
const formatCurrency = formatEur;

// ---------------------------------------------------------------------------
// Cálculo client-side del plan de amortización (solo PREVIEW del dialog;
// la verdad la devuelve el backend al guardar). Lineal sobre la vida útil.
// ---------------------------------------------------------------------------

export function calcularAmortizacionInversion({ importe, anio, mes, vida_util_anios, metodo_amort, pct_amort }) {
  const P = Number(importe);
  if (!P || P <= 0) return [];

  const mesInicio = Math.min(12, Math.max(1, Number(mes) || 1)); // 1-12
  const dotPorAnio = new Map();

  if (metodo_amort === "porcentaje") {
    // F3: % anual del valor. Dotación mensual = importe × pct/100/12, hasta
    // amortizar el importe completo (último mes recortado). Espejo del backend.
    const pct = Number(pct_amort) / 100;
    if (!pct || pct <= 0) return [];
    const dotacionMensual = (P * pct) / 12;
    let acum = 0;
    let m = 0;
    const limite = 12 * 1000; // amplio: cubre coeficientes bajos (1% → 100 años)
    while (acum < P - 1e-6 && m < limite) {
      const dotM = Math.min(dotacionMensual, P - acum);
      const idx = mesInicio - 1 + m;
      const a = Number(anio) + Math.floor(idx / 12);
      dotPorAnio.set(a, (dotPorAnio.get(a) || 0) + dotM);
      acum += dotM;
      m++;
    }
  } else {
    const vida = Number(vida_util_anios);
    if (!vida || vida <= 0) return [];
    const dotacionMensual = P / (vida * 12);
    const totalMeses = vida * 12;
    // Acumula la dotación mensual por año natural desde el mes de adquisición
    // (igual que el backend): primer y último año natural prorrateados.
    for (let m = 0; m < totalMeses; m++) {
      const idx = mesInicio - 1 + m;
      const a = Number(anio) + Math.floor(idx / 12);
      dotPorAnio.set(a, (dotPorAnio.get(a) || 0) + dotacionMensual);
    }
  }

  const filas = [];
  let acumulado = 0;
  Array.from(dotPorAnio.keys())
    .sort((x, y) => x - y)
    .forEach((a) => {
      const dot = dotPorAnio.get(a);
      acumulado += dot;
      filas.push({ anio: a, dotacion: dot, valor_neto: Math.max(0, P - acumulado) });
    });
  return filas;
}

// ---------------------------------------------------------------------------
// Tabla pequeña año / dotación / valor neto
// ---------------------------------------------------------------------------

function TablaAmortizacionInversion({ amortizacion, aniosProyectados = [], compacta = false }) {
  const enHorizonte = useMemo(
    () => new Set((aniosProyectados || []).map(Number)),
    [aniosProyectados]
  );

  if (!amortizacion || amortizacion.length === 0) {
    return (
      <p className="text-xs text-muted-foreground py-2">
        Sin plan de amortización
      </p>
    );
  }

  const totalDotacion = amortizacion.reduce((acc, f) => acc + (f.dotacion || 0), 0);
  const celda = (anio) => cn(enHorizonte.has(Number(anio)) && "bg-primary/5");

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className={cn("w-[80px]", compacta && "h-8 text-xs")}>Año</TableHead>
            <TableHead className={cn("text-right", compacta && "h-8 text-xs")}>Dotación</TableHead>
            <TableHead className={cn("text-right", compacta && "h-8 text-xs")}>Valor neto</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {amortizacion.map((fila) => (
            <TableRow key={fila.anio} className="hover:bg-muted/20">
              <TableCell className={cn("font-medium", compacta && "py-1.5 text-xs", celda(fila.anio))}>
                {fila.anio}
              </TableCell>
              <TableCell className={cn("text-right font-mono tabular-nums text-sm", compacta && "py-1.5 text-xs", celda(fila.anio))}>
                {formatCurrency(fila.dotacion)}
              </TableCell>
              <TableCell className={cn("text-right font-mono tabular-nums text-sm", compacta && "py-1.5 text-xs", celda(fila.anio))}>
                {formatCurrency(fila.valor_neto)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow className="border-t-2 font-semibold bg-primary/5 hover:bg-primary/10">
            <TableCell className={cn(compacta && "py-1.5 text-xs")}>TOTAL</TableCell>
            <TableCell className={cn("text-right font-mono tabular-nums text-sm", compacta && "py-1.5 text-xs")}>
              {formatCurrency(totalDotacion)}
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog crear/editar inversión
// ---------------------------------------------------------------------------

const FORM_VACIO = {
  nombre: "",
  importe: "",
  anio: "",
  mes: "1", // R-8b: mes de adquisición (default enero)
  metodo_amort: "vida_util", // F3: "vida_util" | "porcentaje"
  vida_util_anios: "",
  pct_amort: "", // F3: coeficiente anual (%) cuando metodo_amort = "porcentaje"
  tipo_inmovilizado: "material", // maqueta CAPEX: material | intangible
};

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function InversionDialog({ open, onOpenChange, inversion, aniosProyectados, onGuardar, guardando }) {
  const [form, setForm] = useState(FORM_VACIO);

  useEffect(() => {
    if (open) {
      if (inversion) {
        const p = inversion.params || {};
        setForm({
          nombre: inversion.nombre || "",
          importe: p.importe != null ? String(p.importe) : "",
          anio: p.anio != null ? String(p.anio) : "",
          mes: p.mes != null ? String(p.mes) : "1",
          metodo_amort: p.metodo_amort || "vida_util",
          vida_util_anios: p.vida_util_anios != null ? String(p.vida_util_anios) : "",
          pct_amort: p.pct_amort != null ? String(p.pct_amort) : "",
          tipo_inmovilizado: p.tipo_inmovilizado || "material",
        });
      } else {
        setForm({
          ...FORM_VACIO,
          anio: aniosProyectados[0] != null ? String(aniosProyectados[0]) : "",
        });
      }
    }
  }, [open, inversion, aniosProyectados]);

  const set = (campo) => (e) =>
    setForm((f) => ({ ...f, [campo]: typeof e === "string" ? e : e.target.value }));

  const params = useMemo(() => {
    const base = {
      importe: parseFloat(form.importe),
      anio: parseInt(form.anio),
      mes: parseInt(form.mes) || 1,
      metodo_amort: form.metodo_amort,
      tipo_inmovilizado: form.tipo_inmovilizado || "material",
    };
    if (form.metodo_amort === "porcentaje") {
      base.pct_amort = parseFloat(String(form.pct_amort).replace(",", "."));
    } else {
      base.vida_util_anios = parseInt(form.vida_util_anios);
    }
    return base;
  }, [form]);

  const valido =
    form.nombre.trim() &&
    params.importe > 0 &&
    params.anio > 0 &&
    (form.metodo_amort === "porcentaje"
      ? params.pct_amort > 0 && params.pct_amort <= 100
      : params.vida_util_anios > 0);

  const preview = useMemo(
    () => (valido ? calcularAmortizacionInversion(params) : []),
    [valido, params]
  );

  const handleGuardar = () => {
    onGuardar({ tipo: "inversion", nombre: form.nombre.trim(), params });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{inversion ? "Editar inversión" : "Nueva inversión"}</DialogTitle>
          <DialogDescription>
            Define la inversión prevista. El inmovilizado entra en el balance y la
            amortización anual impacta en el PyG durante la vida útil.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="inversion-nombre">Nombre</Label>
              <Input
                id="inversion-nombre"
                placeholder="Ej.: Nave logística"
                value={form.nombre}
                onChange={set("nombre")}
                data-testid="inversion-nombre"
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Tipo de inmovilizado</Label>
              <Select value={form.tipo_inmovilizado} onValueChange={set("tipo_inmovilizado")}>
                <SelectTrigger data-testid="inversion-tipo-inmovilizado">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="material">Material (tangible)</SelectItem>
                  <SelectItem value="intangible">Intangible</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="inversion-importe">Importe (€)</Label>
              <Input
                id="inversion-importe"
                type="number"
                min="0"
                step="1000"
                placeholder="50000"
                value={form.importe}
                onChange={set("importe")}
                data-testid="inversion-importe"
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Año</Label>
              <Select value={form.anio} onValueChange={set("anio")}>
                <SelectTrigger data-testid="inversion-anio">
                  <SelectValue placeholder="Año" />
                </SelectTrigger>
                <SelectContent>
                  {aniosProyectados.map((anio) => (
                    <SelectItem key={anio} value={String(anio)}>
                      {anio}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Mes de adquisición</Label>
              <Select value={form.mes} onValueChange={set("mes")}>
                <SelectTrigger data-testid="inversion-mes">
                  <SelectValue placeholder="Mes" />
                </SelectTrigger>
                <SelectContent>
                  {MESES.map((nombre, idx) => (
                    <SelectItem key={idx + 1} value={String(idx + 1)}>
                      {nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Método de amortización</Label>
              <Select value={form.metodo_amort} onValueChange={set("metodo_amort")}>
                <SelectTrigger data-testid="inversion-metodo-amort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vida_util">Por vida útil (años)</SelectItem>
                  <SelectItem value="porcentaje">Por % anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.metodo_amort === "porcentaje" ? (
              <div className="grid gap-1.5">
                <Label htmlFor="inversion-pct">% amortización anual</Label>
                <Input
                  id="inversion-pct"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  placeholder="10"
                  value={form.pct_amort}
                  onChange={set("pct_amort")}
                  data-testid="inversion-pct"
                />
              </div>
            ) : (
              <div className="grid gap-1.5">
                <Label htmlFor="inversion-vida">Vida útil (años)</Label>
                <Input
                  id="inversion-vida"
                  type="number"
                  min="1"
                  max="50"
                  step="1"
                  placeholder="10"
                  value={form.vida_util_anios}
                  onChange={set("vida_util_anios")}
                  data-testid="inversion-vida"
                />
              </div>
            )}
          </div>

          {/* Preview del plan de amortización */}
          {preview.length > 0 && (
            <div className="rounded-lg border bg-muted/20 p-3">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Vista previa de la amortización
                </span>
                <Badge variant="outline" className="text-[11px]">
                  Orientativa — el cálculo definitivo se hace al guardar
                </Badge>
              </div>
              <TablaAmortizacionInversion
                amortizacion={preview}
                aniosProyectados={aniosProyectados}
                compacta
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={!valido || guardando} data-testid="inversion-guardar">
            {guardando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {inversion ? "Guardar cambios" : "Crear inversión"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Card de una inversión
// ---------------------------------------------------------------------------

function InversionCard({ inversion, aniosProyectados, readOnly, onEditar, onBorrar }) {
  const [abierto, setAbierto] = useState(false);
  const p = inversion.params || {};
  const esIntangible = p.tipo_inmovilizado === "intangible";
  // Cuenta contable de la dotación según el tipo (email del cliente 2026-08-19):
  // 680 amortización del intangible · 681 amortización del material.
  const cuentaAmort = esIntangible ? "680" : "681";
  const esPorcentaje = p.metodo_amort === "porcentaje";
  const amortAnual = esPorcentaje
    ? (p.importe > 0 && p.pct_amort > 0 ? (p.importe * p.pct_amort) / 100 : null)
    : (p.importe > 0 && p.vida_util_anios > 0 ? p.importe / p.vida_util_anios : null);

  return (
    <Card data-testid={`inversion-card-${inversion.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary shrink-0" />
              <h3 className="font-semibold truncate">{inversion.nombre}</h3>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Badge variant="secondary" className="font-mono text-xs">
                {formatCurrency(p.importe)}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                en {p.mes && p.mes !== 1 ? `${MESES[p.mes - 1]} ` : ""}{p.anio}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {esPorcentaje
                  ? `${p.pct_amort}% anual`
                  : `vida útil ${p.vida_util_anios} año${p.vida_util_anios !== 1 ? "s" : ""}`}
              </Badge>
              <Badge
                variant="outline"
                className={cn(
                  "text-xs gap-1",
                  esIntangible
                    ? "text-violet-700 dark:text-violet-400 border-violet-300 dark:border-violet-800"
                    : "text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800"
                )}
              >
                {esIntangible ? <Cloud className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
                {esIntangible ? "Intangible" : "Material"}
              </Badge>
              <Badge variant="secondary" className="font-mono text-xs">
                cuenta {cuentaAmort}
              </Badge>
            </div>
          </div>
          {!readOnly && (
            <div className="flex gap-1 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onEditar(inversion)}
                data-testid={`inversion-editar-${inversion.id}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={() => onBorrar(inversion)}
                data-testid={`inversion-borrar-${inversion.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Badges de impacto */}
        <div className="flex flex-wrap gap-1.5 mt-2">
          <Badge variant="outline" className="text-[11px] gap-1 text-sky-700 dark:text-sky-400 border-sky-300 dark:border-sky-800">
            <Scale className="h-3 w-3" />
            → Balance: inmovilizado
          </Badge>
          <Badge variant="outline" className="text-[11px] gap-1 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800">
            <TrendingDown className="h-3 w-3" />
            → PyG: amortización {amortAnual != null ? `${formatCurrency(amortAnual)}/año` : ""}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Collapsible open={abierto} onOpenChange={setAbierto}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="px-0 text-xs text-muted-foreground hover:bg-transparent">
              <ChevronDown
                className={cn("h-3.5 w-3.5 mr-1 transition-transform", abierto && "rotate-180")}
              />
              Plan de amortización
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="pt-2 max-w-md">
              <TablaAmortizacionInversion
                amortizacion={inversion.amortizacion}
                aniosProyectados={aniosProyectados}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export default function CapexContent({ empresaId, ejercicios, aniosProyectar, readOnly }) {
  const [operaciones, setOperaciones] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inversionEditando, setInversionEditando] = useState(null);
  const [inversionBorrando, setInversionBorrando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  // Subapartado activo (maqueta CAPEX 2026-08-19)
  const [subseccion, setSubseccion] = useState("inversiones");

  // Años proyectados: los del MODELO (`anios_proyectados` del consolidado, que
  // ya aplica el año base y el horizonte) — una sola fuente, así el alta de
  // «Nueva inversión» propone el primer año proyectado y no un año real
  // (petición de Pau, nov-2026). Mientras carga, se derivan de los ejercicios.
  const aniosModelo = (resultado?.anios_proyectados || []).join(",");
  const aniosProyectados = useMemo(() => {
    if (aniosModelo) return aniosModelo.split(",").map(Number).sort((a, b) => a - b);
    const ultimoAnio =
      ejercicios && ejercicios.length > 0
        ? Math.max(...ejercicios.map((e) => e.anio))
        : new Date().getFullYear();
    const n = Math.max(1, aniosProyectar || 1);
    return Array.from({ length: n }, (_, i) => ultimoAnio + 1 + i);
  }, [aniosModelo, ejercicios, aniosProyectar]);

  const cargar = useCallback(async () => {
    try {
      setError(null);
      const res = await getProyeccionOperaciones(empresaId);
      setOperaciones(res.data || []);
      // Serie de amortización histórica + total (R-8a/R-8c). No es crítica: si
      // falla, las inversiones siguen mostrándose igual.
      try {
        const resResultado = await getProyeccionResultado(empresaId);
        setResultado(resResultado.data || null);
      } catch {
        setResultado(null);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al cargar las inversiones");
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    setLoading(true);
    cargar();
  }, [cargar]);

  const inversiones = useMemo(
    () => operaciones.filter((op) => op.tipo === "inversion"),
    [operaciones]
  );

  // R-8a / R-8c: serie de amortización histórica (activos ya existentes) y
  // total (histórica + nuevas inversiones) por año, expuesta por el backend.
  const amortCapex = resultado?.amortizacion_capex;
  const evolInmov = resultado?.evolucion_inmovilizado;
  const amortAnios = useMemo(
    () => [...(amortCapex?.anios || [])].map(Number).sort((a, b) => a - b),
    [amortCapex]
  );
  const aniosProySet = useMemo(
    () => new Set((aniosProyectados || []).map(Number)),
    [aniosProyectados]
  );

  const handleNuevo = () => {
    setInversionEditando(null);
    setDialogOpen(true);
  };

  const handleEditar = (inversion) => {
    setInversionEditando(inversion);
    setDialogOpen(true);
  };

  const handleGuardar = async (datos) => {
    try {
      setGuardando(true);
      if (inversionEditando) {
        await actualizarProyeccionOperacion(empresaId, inversionEditando.id, {
          nombre: datos.nombre,
          params: datos.params,
        });
        toast.success("Inversión actualizada");
      } else {
        await crearProyeccionOperacion(empresaId, datos);
        toast.success("Inversión creada");
      }
      setDialogOpen(false);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar la inversión");
    } finally {
      setGuardando(false);
    }
  };

  const handleBorrar = async () => {
    if (!inversionBorrando) return;
    try {
      setBorrando(true);
      await eliminarProyeccionOperacion(empresaId, inversionBorrando.id);
      toast.success("Inversión eliminada");
      setInversionBorrando(null);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar la inversión");
    } finally {
      setBorrando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="capex-content">
      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Inversiones en activos fijos previstas: el inmovilizado entra en el balance y
          se amortiza linealmente contra el PyG durante su vida útil.
        </p>
        {!readOnly && (
          <Button onClick={handleNuevo} className="shrink-0" data-testid="nueva-inversion">
            <Plus className="h-4 w-4 mr-1" />
            Nueva inversión
          </Button>
        )}
      </div>

      {/* Sub-navegación en 4 subapartados (maqueta CAPEX 2026-08-19) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { id: "inversiones", icon: Building2, titulo: "Nuevas inversiones", sub: "altas de inversiones a proyectar" },
          { id: "amort_hist", icon: History, titulo: "Amortización histórica", sub: "amortización del inmovilizado existente" },
          { id: "resumen_amort", icon: Layers, titulo: "Resumen amortización total", sub: "histórica + nuevas inversiones" },
          { id: "evolucion", icon: TrendingUp, titulo: "Evolución del inmovilizado en balance", sub: "impacto en balance del inmovilizado" },
        ].map(({ id, icon: Icon, titulo, sub }) => (
          <button
            key={id}
            type="button"
            onClick={() => setSubseccion(id)}
            data-testid={`capex-subseccion-${id}`}
            className={cn(
              "flex items-start gap-3 rounded-lg border p-4 text-left transition-colors hover:bg-muted/30",
              subseccion === id ? "border-primary ring-1 ring-primary bg-primary/5" : "bg-card"
            )}
          >
            <Icon className={cn("h-5 w-5 shrink-0 mt-0.5", subseccion === id ? "text-primary" : "text-muted-foreground")} />
            <span className="min-w-0">
              <span className="block font-semibold text-sm">{titulo}</span>
              <span className="block text-xs text-muted-foreground truncate">{sub}</span>
            </span>
          </button>
        ))}
      </div>

      {/* ── 1. NUEVAS INVERSIONES ── */}
      {subseccion === "inversiones" &&
      (inversiones.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center border rounded-lg bg-muted/20">
          <div className="p-4 rounded-full bg-muted mb-4">
            <Building2 className="h-10 w-10 text-muted-foreground" />
          </div>
          <h3 className="font-semibold mb-1">Sin inversiones planificadas</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Añade una inversión prevista y verás su efecto en el inmovilizado (balance),
            la amortización (PyG) y la salida de caja.
          </p>
          {!readOnly && (
            <Button variant="outline" className="mt-4" onClick={handleNuevo}>
              <Plus className="h-4 w-4 mr-1" />
              Nueva inversión
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {inversiones.map((inversion) => (
            <InversionCard
              key={inversion.id}
              inversion={inversion}
              aniosProyectados={aniosProyectados}
              readOnly={readOnly}
              onEditar={handleEditar}
              onBorrar={setInversionBorrando}
            />
          ))}
        </div>
      ))}

      {/* ── 2. AMORTIZACIÓN HISTÓRICA (driver 68, puntos 5/9) ── */}
      {subseccion === "amort_hist" && (
      <TablaDrivers
        empresaId={empresaId}
        ejercicios={ejercicios}
        aniosProyectar={aniosProyectar}
        seccionPyg="gastos"
        filtroCodigos="68"
        storageKey={`finreport:nivel:${empresaId}:capex-amort`}
        nivelesPermitidos={["3D", "MAX"]}
        tiposDriverPermitidos={["valor_fijo"]}
        ocultarValorFijoIpc
        onDriverSaved={cargar}
        readOnly={readOnly}
        titulo={null}
        descripcion={null}
      />
      )}

      {/* ── 3. RESUMEN AMORTIZACIÓN TOTAL ── */}
      {subseccion === "resumen_amort" && amortCapex && amortAnios.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <History className="h-4 w-4 text-primary" />
              Amortización histórica y total
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="min-w-[200px]">Concepto</TableHead>
                  {amortAnios.map((a) => (
                    <TableHead
                      key={a}
                      className={cn(
                        "text-right w-[110px]",
                        aniosProySet.has(a) && "bg-proy text-proy font-semibold"
                      )}
                    >
                      {a}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  { clave: "historica", label: "Amortización histórica" },
                  { clave: "nueva", label: "Amortización nuevas inversiones" },
                  { clave: "total", label: "Amortización total", destacada: true },
                ].map(({ clave, label, destacada }) => (
                  <Fragment key={clave}>
                    <TableRow
                      className={cn(
                        "hover:bg-muted/20",
                        destacada && "border-t-2 font-semibold bg-primary/5 hover:bg-primary/10"
                      )}
                    >
                      <TableCell className="font-medium">{label}</TableCell>
                      {amortAnios.map((a) => {
                        const serie = amortCapex[clave] || {};
                        const v = serie[a] ?? serie[String(a)] ?? null;
                        return (
                          <TableCell
                            key={a}
                            className={cn(
                              "text-right font-mono tabular-nums text-sm",
                              aniosProySet.has(a) && "bg-proy"
                            )}
                          >
                            {formatCurrency(v)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {/* W20.12c: detalle del cálculo bajo "histórica" — el
                        reparto por tipo (680 intangible / 681-682 material):
                        histórico por las cuentas reales, proyección por el
                        driver de cada cuenta. */}
                    {clave === "historica" && amortCapex.historica_por_tipo &&
                      [
                        { tipo: "intangible", label: "└ Intangible (cuenta 680)" },
                        { tipo: "material", label: "└ Material (cuentas 681/682)" },
                      ].map(({ tipo, label: tlabel }) => (
                        <TableRow key={`hpt-${tipo}`} className="hover:bg-muted/10">
                          <TableCell className="pl-8 text-xs text-muted-foreground">
                            {tlabel}
                          </TableCell>
                          {amortAnios.map((a) => {
                            const serie = amortCapex.historica_por_tipo[tipo] || {};
                            const v = serie[a] ?? serie[String(a)] ?? null;
                            return (
                              <TableCell
                                key={a}
                                className={cn(
                                  "text-right font-mono tabular-nums text-xs text-muted-foreground",
                                  aniosProySet.has(a) && "bg-proy"
                                )}
                              >
                                {v != null ? formatCurrency(v) : "—"}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    {/* F4: desglose activo a activo bajo "nuevas inversiones" */}
                    {clave === "nueva" &&
                      (amortCapex.por_activo || []).map((act, i) => (
                        <TableRow key={`act-${i}`} className="hover:bg-muted/10">
                          <TableCell className="pl-8 text-xs text-muted-foreground">
                            └ {act.nombre}
                            {act.anio_alta ? (
                              <span className="ml-1 opacity-70">(alta {act.anio_alta})</span>
                            ) : null}
                          </TableCell>
                          {amortAnios.map((a) => {
                            const v = (act.anios || {})[a] ?? (act.anios || {})[String(a)] ?? null;
                            return (
                              <TableCell
                                key={a}
                                className={cn(
                                  "text-right font-mono tabular-nums text-xs text-muted-foreground",
                                  aniosProySet.has(a) && "bg-proy"
                                )}
                              >
                                {v ? formatCurrency(v) : "—"}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
            <p className="px-4 py-2 text-[11px] text-muted-foreground">
              La amortización histórica corresponde a los activos ya existentes en
              balance (driver de amortización); la total suma además la dotación de las
              inversiones planificadas en esta pestaña.
            </p>
            {amortCapex.nota && (
              <p className="px-4 pb-2 text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <AlertCircle className="h-3 w-3 shrink-0" />
                {amortCapex.nota}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── 4. EVOLUCIÓN DEL INMOVILIZADO EN BALANCE (punto 10 + desglose
          intangible/material de la maqueta 2026-08-19) ── */}
      {subseccion === "evolucion" && evolInmov && evolInmov.filas && evolInmov.filas.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              Evolución del inmovilizado en balance
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Impacto de las inversiones y la amortización en el inmovilizado neto del
              balance.
            </p>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {evolInmov.por_tipo && evolInmov.por_tipo.bloques ? (
              (() => {
                const pt = evolInmov.por_tipo;
                const aniosHist = pt.anios_historicos || [];
                const aniosProy = pt.anios_proyectados || [];
                const todos = [...aniosHist, ...aniosProy];
                // Literales calcados de la maqueta 2026-08-19: cada fila lleva el
                // sufijo de su bloque (p.ej. "+ Nuevas inversiones intangible",
                // "= Inmovilizado material neto final").
                const FILAS_POR_BLOQUE = {
                  intangible: [
                    { clave: "inicial", label: "Saldo inicial" },
                    { clave: "inversiones", label: "+ Nuevas inversiones intangible" },
                    { clave: "amortizacion", label: "− Amortización intangible", signo: "-" },
                    { clave: "final", label: "= Inmovilizado intangible neto final", destacada: true },
                  ],
                  material: [
                    { clave: "inicial", label: "Saldo inicial" },
                    { clave: "inversiones", label: "+ Nuevas inversiones material" },
                    { clave: "amortizacion", label: "− Amortización material", signo: "-" },
                    { clave: "final", label: "= Inmovilizado material neto final", destacada: true },
                  ],
                  total: [
                    { clave: "inicial", label: "Saldo inicial total" },
                    { clave: "inversiones", label: "+ Nuevas inversiones totales" },
                    { clave: "amortizacion", label: "− Amortización total", signo: "-" },
                    { clave: "final", label: "= Inmovilizado neto total final", destacada: true },
                  ],
                };
                const BLOQUES = [
                  { id: "intangible", label: "Inmovilizado intangible", icono: Cloud },
                  { id: "material", label: "Inmovilizado material (tangible)", icono: Building2 },
                  { id: "total", label: "Inmovilizado neto total", icono: Package, total: true },
                ];
                const celda = (v, proy, destacada) => (
                  <TableCell
                    className={cn(
                      "text-right font-mono tabular-nums text-sm",
                      proy && "bg-primary/5",
                      destacada && "font-semibold"
                    )}
                  >
                    {formatCurrency(v)}
                  </TableCell>
                );
                return (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/20">
                        <TableHead />
                        {aniosHist.length > 0 && (
                          <TableHead colSpan={aniosHist.length} className="text-center text-[11px] tracking-widest text-muted-foreground">
                            HISTÓRICO
                          </TableHead>
                        )}
                        {aniosProy.length > 0 && (
                          <TableHead colSpan={aniosProy.length} className="text-center text-[11px] tracking-widest text-proy">
                            PROYECCIÓN
                          </TableHead>
                        )}
                      </TableRow>
                      <TableRow className="bg-muted/30">
                        <TableHead className="min-w-[240px]">Concepto</TableHead>
                        {todos.map((a) => (
                          <TableHead
                            key={a}
                            className={cn(
                              "text-right w-[105px]",
                              aniosProy.includes(a) && "bg-proy text-proy font-semibold"
                            )}
                          >
                            {a}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {BLOQUES.map(({ id, label, icono: Icono, total }) => (
                        <Fragment key={id}>
                          <TableRow className={cn("hover:bg-transparent", total ? "bg-amber-500/10" : "bg-muted/20")}>
                            <TableCell colSpan={todos.length + 1} className={cn("font-semibold text-sm py-2", total ? "text-amber-700 dark:text-amber-400" : "text-primary")}>
                              <span className="inline-flex items-center gap-1.5">
                                <Icono className="h-4 w-4" />
                                {label}
                              </span>
                            </TableCell>
                          </TableRow>
                          {FILAS_POR_BLOQUE[id].map(({ clave, label: flabel, signo, destacada }) => (
                            <TableRow key={`${id}-${clave}`} className={cn("hover:bg-muted/20", destacada && "border-b-2 font-semibold")}>
                              <TableCell className={cn("text-sm", destacada ? "font-semibold" : "pl-6 text-muted-foreground")}>
                                {flabel}
                              </TableCell>
                              {todos.map((a) => {
                                const blk = (pt.bloques[id] || {})[String(a)] || {};
                                let v = blk[clave];
                                if (v !== null && v !== undefined && signo === "-") v = -Math.abs(v);
                                return (
                                  <Fragment key={a}>
                                    {celda(v, aniosProy.includes(a), destacada)}
                                  </Fragment>
                                );
                              })}
                            </TableRow>
                          ))}
                        </Fragment>
                      ))}
                    </TableBody>
                  </Table>
                );
              })()
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="min-w-[220px]">Concepto</TableHead>
                    {evolInmov.filas.map((f) => (
                      <TableHead
                        key={f.anio}
                        className="text-right w-[110px] bg-primary/5 text-primary font-semibold"
                      >
                        {f.anio}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    { clave: "inicial", label: "Inmovilizado neto inicial", signo: "" },
                    { clave: "capex", label: "+ CAPEX (inversiones)", signo: "+" },
                    { clave: "amortizacion", label: "− Amortización (histórica + nueva)", signo: "-" },
                    { clave: "final", label: "= Inmovilizado neto final", destacada: true },
                  ].map(({ clave, label, signo, destacada }) => (
                    <TableRow
                      key={clave}
                      className={cn(
                        "hover:bg-muted/20",
                        destacada && "border-t-2 font-semibold bg-primary/5 hover:bg-primary/10"
                      )}
                    >
                      <TableCell className="font-medium">{label}</TableCell>
                      {evolInmov.filas.map((f) => {
                        const v = f[clave] ?? 0;
                        const shown = signo === "-" ? -Math.abs(v) : v;
                        return (
                          <TableCell
                            key={f.anio}
                            className="text-right font-mono tabular-nums text-sm bg-primary/5"
                          >
                            {formatCurrency(shown)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <p className="px-4 py-2 text-[11px] text-muted-foreground flex items-start gap-1.5">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                Cada año parte del inmovilizado neto del año anterior, se suman las
                inversiones y se resta la amortización total (histórica + nuevas
                inversiones). Resultados expresados en euros (€).
                <span className="block mt-0.5 opacity-80">
                  El total coincide con el "Inmovilizado neto" del balance proyectado.
                  En el histórico, la amortización por tipo sale de las cuentas
                  680/681/682 reales; en proyección, del driver de cada cuenta
                  (680 → intangible; 681/682 → material) más la dotación de cada
                  inversión según su tipo. Si un tipo agota su valor neto, su
                  dotación se limita en este desglose (el PyG no cambia).
                </span>
              </span>
            </p>
            {/* W20.12b: aviso de tope — un tipo agotó su valor neto. */}
            {evolInmov.por_tipo?.tope_aplicado &&
              Object.keys(evolInmov.por_tipo.tope_aplicado).length > 0 && (
                <p className="px-4 pb-2 text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  {Object.entries(evolInmov.por_tipo.tope_aplicado)
                    .map(([t, anios]) => `Amortización ${t} agotada en ${anios[0]}`)
                    .join(" · ")}
                  : la dotación de ese tipo se limita al valor neto disponible (el
                  total del PyG no cambia).
                </p>
              )}
            {/* Procedencia de la base: de dónde sale el importe inicial. */}
            <div className="px-4 pb-3 text-[11px] text-muted-foreground border-t pt-2">
              <p className="font-medium text-foreground/80">
                ¿De dónde sale el importe inicial{evolInmov.base_anio ? ` (${formatCurrency(evolInmov.base)} a cierre de ${evolInmov.base_anio})` : ""}?
              </p>
              <p className="mt-0.5">
                Es el <strong>inmovilizado neto amortizable</strong> del balance real
                del último año cerrado (intangible + material + inversiones
                inmobiliarias). El resto del Activo No Corriente (inversiones
                financieras, impuesto diferido…) no se amortiza y va en "Otros
                activos", constante
                {Object.keys(evolInmov.base_desglose || {}).length > 0 ? ":" : "."}
              </p>
              {Object.keys(evolInmov.base_desglose || {}).length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {Object.entries(evolInmov.base_desglose).map(([partida, saldo]) => (
                    <li key={partida} className="flex justify-between gap-4 font-mono tabular-nums">
                      <span className="font-sans">{partida}</span>
                      <span>{formatCurrency(saldo)}</span>
                    </li>
                  ))}
                  <li className="flex justify-between gap-4 font-mono tabular-nums border-t pt-0.5 font-semibold">
                    <span className="font-sans">Total inmovilizado neto</span>
                    <span>{formatCurrency(evolInmov.base)}</span>
                  </li>
                </ul>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog crear/editar */}
      <InversionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        inversion={inversionEditando}
        aniosProyectados={aniosProyectados}
        onGuardar={handleGuardar}
        guardando={guardando}
      />

      {/* Confirmación de borrado */}
      <AlertDialog open={!!inversionBorrando} onOpenChange={(o) => !o && setInversionBorrando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar inversión?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará «{inversionBorrando?.nombre}» y su impacto en el balance, el PyG
              y la tesorería proyectados. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={borrando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleBorrar();
              }}
              disabled={borrando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {borrando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
